import { Node as ProseMirrorNode } from '@tiptap/pm/model'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { ReplaceStep } from '@tiptap/pm/transform'
import { Decoration, DecorationSet, EditorView } from '@tiptap/pm/view'
import {
  CallResourceCitation,
  CallSentenceCitation,
  CitationMinuteNode,
  CitationMinuteNodeChunk,
  CitationMinuteNodes,
} from '../../../../../../backend/src/citations/minutes-citations-api.dto'

export type ResourceInfo = {
  id: number
  fileName: string
}

export type CitationPluginOptions = {
  getCitations: () => CitationMinuteNodes
  getResources: () => ResourceInfo[]
  formatPageLabel: (page: number) => string
  onOpenDocument: ((event: { resourceId: number; resourcePageNumber: number }) => void) | null
  onOpenMeeting: ((event: { startSentenceId: string; endSentenceId: string }) => void) | null
}

type CitationPluginState = {
  decorations: DecorationSet
  highlightedChunkIds: string[]
  highlightType: 'sentence' | 'resource' | null
  decorationsBuiltWithContent: boolean
  rebuildPending: boolean
}

type SetHighlightMeta = {
  type: 'SET_HIGHLIGHT'
  chunkIds: string[]
  citationType: 'sentence' | 'resource'
}

type ClearHighlightMeta = {
  type: 'CLEAR_HIGHLIGHT'
}

type RebuildMeta = {
  type: 'REBUILD'
}

type CitationMeta = SetHighlightMeta | ClearHighlightMeta | RebuildMeta

export const citationPluginKey = new PluginKey<CitationPluginState>('citation')

// An empty ProseMirror document still has a non-zero content.size due to
// structural node boundaries (e.g. an empty paragraph = size 2). When the
// editor initializes with Yjs, the document starts as an empty skeleton before
// sync delivers the real content. This threshold distinguishes between the
// empty skeleton and a document with actual content.
const EMPTY_DOCUMENT_SIZE_THRESHOLD = 4
const CITATION_REBUILD_DEBOUNCE_MS = 150

type ChunkRange = {
  chunkId: string
  from: number
  to: number
}

type CitationEntry = {
  type: 'sentence' | 'resource'
  chunkId: string
  duplicateChunkIds: string[]
  sentenceCitation: CallSentenceCitation | null
  resourceCitation: CallResourceCitation | null
  resourceName: string | null
}

type ParagraphCitations = {
  paragraphEnd: number
  entries: CitationEntry[]
}

function findChunkRangesInMinuteNode(minuteNodePos: number, minuteNode: ProseMirrorNode): ChunkRange[] {
  const ranges: ChunkRange[] = []

  minuteNode.descendants((node, pos) => {
    if (!node.isText) {
      return true
    }

    const chunkMark = node.marks.find((mark) => mark.type.name === 'chunk') ?? null

    if (chunkMark === null) {
      return false
    }

    const chunkId = chunkMark.attrs['chunkId'] as string | null

    if (chunkId === null) {
      return false
    }

    ranges.push({
      chunkId,
      from: minuteNodePos + pos + 1,
      to: minuteNodePos + pos + 1 + node.nodeSize,
    })

    return false
  })

  return ranges
}

type ParagraphRange = {
  from: number
  end: number
}

function findParagraphRangesInMinuteNode(minuteNodePos: number, minuteNode: ProseMirrorNode): ParagraphRange[] {
  const ranges: ParagraphRange[] = []

  minuteNode.descendants((node, pos) => {
    if (node.type.name !== 'paragraph') {
      return true
    }

    const absoluteFrom = minuteNodePos + pos + 1
    const absoluteTo = absoluteFrom + node.nodeSize

    ranges.push({ from: absoluteFrom, end: absoluteTo - 1 })
    return false
  })

  return ranges
}

function buildCitationEntries(
  citationNode: CitationMinuteNode,
  chunkRanges: ChunkRange[],
  resourceMap: Map<number, ResourceInfo>,
  paragraphRanges: ParagraphRange[]
): ParagraphCitations[] {
  const chunkRangeMap = new Map<string, ChunkRange>()
  for (const range of chunkRanges) {
    chunkRangeMap.set(range.chunkId, range)
  }

  const paragraphMap = new Map<number, ParagraphCitations>()

  for (const [, item] of Object.entries(citationNode.items)) {
    for (const [chunkId, chunk] of Object.entries(item.chunks)) {
      const matchingRange = chunkRangeMap.get(chunkId) ?? null

      if (matchingRange === null) {
        console.warn(`Could not find chunk with ID '${chunkId}' in the document, it might have been removed from the minutes.`)
        continue
      }

      const paragraphEnd = paragraphRanges.find((paragraph) => matchingRange.from >= paragraph.from && matchingRange.from < paragraph.end + 1)?.end ?? null

      if (paragraphEnd === null) {
        continue
      }

      const entries = buildEntriesForChunk(chunkId, chunk, resourceMap)

      if (entries.length === 0) {
        continue
      }

      const existing = paragraphMap.get(paragraphEnd) ?? null

      if (existing === null) {
        paragraphMap.set(paragraphEnd, { paragraphEnd, entries })
      } else {
        existing.entries.push(...entries)
      }
    }
  }

  return Array.from(paragraphMap.values())
}

function buildEntriesForChunk(chunkId: string, chunk: CitationMinuteNodeChunk, resourceMap: Map<number, ResourceInfo>): CitationEntry[] {
  const entries: CitationEntry[] = []

  for (const sentenceCitation of chunk.callSentenceCitations) {
    entries.push({
      type: 'sentence',
      chunkId,
      duplicateChunkIds: sentenceCitation.duplicateChunkIds,
      sentenceCitation,
      resourceCitation: null,
      resourceName: null,
    })
  }

  for (const resourceCitation of chunk.callResourceCitations) {
    const resource = resourceMap.get(resourceCitation.resourceId) ?? null

    if (resource === null) {
      console.warn(`Could not find resource with ID ${resourceCitation.resourceId} for call resource in chunk with ID ${chunkId}.`)
      continue
    }

    entries.push({
      type: 'resource',
      chunkId,
      duplicateChunkIds: resourceCitation.duplicateChunkIds,
      sentenceCitation: null,
      resourceCitation,
      resourceName: resource.fileName,
    })
  }

  return entries
}

function createWidgetDom(entries: CitationEntry[], labelStart: number, view: EditorView, options: CitationPluginOptions): HTMLElement {
  const group = document.createElement('span')
  group.className = 'citation-group'
  group.contentEditable = 'false'

  entries.forEach((entry, index) => {
    const badge = document.createElement('span')
    const label = labelStart + index

    badge.className = entry.type === 'sentence' ? 'citation-group-item citation-call-sentence' : 'citation-group-item citation-call-resource'
    badge.textContent = label.toString()

    const allChunkIds = [entry.chunkId, ...entry.duplicateChunkIds]

    badge.addEventListener('mouseenter', () => {
      view.dispatch(
        view.state.tr.setMeta(citationPluginKey, {
          type: 'SET_HIGHLIGHT',
          chunkIds: allChunkIds,
          citationType: entry.type,
        } satisfies CitationMeta)
      )

      if (entry.type === 'resource' && entry.resourceCitation !== null && entry.resourceName !== null) {
        showResourceTooltip(badge, entry.resourceName, entry.resourceCitation.resourcePageNumber, options.formatPageLabel, view)
      }
    })

    badge.addEventListener('mouseleave', () => {
      view.dispatch(view.state.tr.setMeta(citationPluginKey, { type: 'CLEAR_HIGHLIGHT' } satisfies CitationMeta))

      removeResourceTooltip(view)
    })

    badge.addEventListener('click', () => {
      if (entry.type === 'sentence' && entry.sentenceCitation !== null && options.onOpenMeeting !== null) {
        options.onOpenMeeting({
          startSentenceId: entry.sentenceCitation.startSentenceId,
          endSentenceId: entry.sentenceCitation.endSentenceId,
        })
      } else if (entry.type === 'resource' && entry.resourceCitation !== null && options.onOpenDocument !== null) {
        options.onOpenDocument({
          resourceId: entry.resourceCitation.resourceId,
          resourcePageNumber: entry.resourceCitation.resourcePageNumber,
        })
      }
    })

    group.appendChild(badge)
  })

  return group
}

function getTooltipContainer(view: EditorView): HTMLElement {
  return view.dom.parentElement ?? view.dom
}

const scrollHandlers = new WeakMap<EditorView, () => void>()

function showResourceTooltip(badge: HTMLElement, resourceName: string, pageNumber: number, formatPageLabel: (page: number) => string, view: EditorView) {
  removeResourceTooltip(view)

  const container = getTooltipContainer(view)

  const tooltip = document.createElement('div')
  tooltip.className = 'citation-tooltip'
  tooltip.textContent = `${resourceName} (${formatPageLabel(pageNumber)})`

  container.appendChild(tooltip)

  const badgeRect = badge.getBoundingClientRect()

  let left = badgeRect.left
  const top = badgeRect.bottom + 4

  tooltip.style.left = `${left}px`
  tooltip.style.top = `${top}px`

  const tooltipWidth = tooltip.offsetWidth
  const viewportWidth = window.innerWidth

  if (left + tooltipWidth > viewportWidth - 10) {
    left = viewportWidth - tooltipWidth - 10
  }

  if (left < 10) {
    left = 10
  }

  tooltip.style.left = `${left}px`
  tooltip.classList.add('visible')

  const scrollHandler = () => removeResourceTooltip(view)
  scrollHandlers.set(view, scrollHandler)
  view.dom.addEventListener('scroll', scrollHandler, { once: true })
}

function removeResourceTooltip(view: EditorView) {
  const scrollHandler = scrollHandlers.get(view) ?? null

  if (scrollHandler !== null) {
    view.dom.removeEventListener('scroll', scrollHandler)
    scrollHandlers.delete(view)
  }

  const container = getTooltipContainer(view)
  const existing = container.querySelector('.citation-tooltip')

  if (existing !== null) {
    existing.classList.remove('visible')
    existing.remove()
  }
}

function buildResourceMap(resources: ResourceInfo[]): Map<number, ResourceInfo> {
  const map = new Map<number, ResourceInfo>()
  for (const resource of resources) {
    map.set(resource.id, resource)
  }
  return map
}

function buildDecorationSet(
  doc: ProseMirrorNode,
  options: CitationPluginOptions,
  resourceMap: Map<number, ResourceInfo>,
  highlightedChunkIds: string[],
  highlightType: 'sentence' | 'resource' | null
): DecorationSet {
  const decorations: Decoration[] = []
  const highlightedSet = highlightedChunkIds.length > 0 ? new Set(highlightedChunkIds) : null
  const citations = options.getCitations()

  doc.descendants((node, pos) => {
    if (node.type.name !== 'listItem') {
      return true
    }

    const nodeId = node.attrs['nodeId'] as string | null

    if (nodeId === null) {
      return true
    }

    const citationNode = citations[nodeId] ?? null

    if (citationNode === null) {
      return true
    }

    const chunkRanges = findChunkRangesInMinuteNode(pos, node)
    const paragraphRanges = findParagraphRangesInMinuteNode(pos, node)
    const paragraphCitations = buildCitationEntries(citationNode, chunkRanges, resourceMap, paragraphRanges)

    let labelCounter = 1

    for (const paragraphCitation of paragraphCitations) {
      const labelStart = labelCounter

      const widget = Decoration.widget(
        paragraphCitation.paragraphEnd,
        (currentView) => createWidgetDom(paragraphCitation.entries, labelStart, currentView, options),
        { key: `citation-${paragraphCitation.paragraphEnd}` }
      )
      decorations.push(widget)

      labelCounter += paragraphCitation.entries.length
    }

    if (highlightedSet !== null) {
      for (const range of chunkRanges) {
        if (highlightedSet.has(range.chunkId)) {
          const highlightClass =
            highlightType === 'resource' ? 'citation-highlight citation-call-resource-highlight' : 'citation-highlight citation-call-sentence-highlight'

          decorations.push(Decoration.inline(range.from, range.to, { class: highlightClass }))
        }
      }
    }

    return false
  })

  return DecorationSet.create(doc, decorations)
}

export function createCitationPlugin(options: CitationPluginOptions): Plugin {
  let resourceMap = buildResourceMap(options.getResources())

  function rebuildDecorations(doc: ProseMirrorNode, highlightedChunkIds: string[], highlightType: 'sentence' | 'resource' | null): CitationPluginState {
    return {
      decorations: buildDecorationSet(doc, options, resourceMap, highlightedChunkIds, highlightType),
      highlightedChunkIds,
      highlightType,
      decorationsBuiltWithContent: doc.content.size > EMPTY_DOCUMENT_SIZE_THRESHOLD,
      rebuildPending: false,
    }
  }

  return new Plugin<CitationPluginState>({
    key: citationPluginKey,

    state: {
      init(_, state) {
        return rebuildDecorations(state.doc, [], null)
      },

      apply(transaction, pluginState, oldState, newState) {
        const meta = (transaction.getMeta(citationPluginKey) as CitationMeta | undefined) ?? null

        if (meta !== null) {
          if (meta.type === 'SET_HIGHLIGHT') {
            return rebuildDecorations(newState.doc, meta.chunkIds, meta.citationType)
          }

          if (meta.type === 'CLEAR_HIGHLIGHT') {
            return rebuildDecorations(newState.doc, [], null)
          }

          if (meta.type === 'REBUILD') {
            resourceMap = buildResourceMap(options.getResources())
            return rebuildDecorations(newState.doc, pluginState.highlightedChunkIds, pluginState.highlightType)
          }
        }

        if (transaction.docChanged) {
          const allStepsAreSimpleReplaces =
            pluginState.decorationsBuiltWithContent &&
            transaction.steps.every(
              (step) =>
                step instanceof ReplaceStep && step.slice.openStart === 0 && step.slice.openEnd === 0 && step.to - step.from < oldState.doc.content.size / 2
            )

          if (allStepsAreSimpleReplaces) {
            const deletesIntoNodeWithCitations = transaction.steps.some((step) => {
              if (!(step instanceof ReplaceStep)) {
                return false
              }
              const mappedPos = transaction.mapping.mapResult(step.from)
              const resolvedPos = newState.doc.resolve(mappedPos.pos)
              for (let depth = resolvedPos.depth; depth >= 0; depth--) {
                const ancestor = resolvedPos.node(depth)
                if (ancestor.type.name === 'listItem' && ancestor.attrs['nodeId'] !== null) {
                  return true
                }
              }
              return false
            })

            if (!deletesIntoNodeWithCitations) {
              return {
                decorations: pluginState.decorations.map(transaction.mapping, newState.doc),
                highlightedChunkIds: pluginState.highlightedChunkIds,
                highlightType: pluginState.highlightType,
                decorationsBuiltWithContent: true,
                rebuildPending: false,
              }
            }
          }

          const isFullDocumentReplace = transaction.steps.some(
            (step) => step instanceof ReplaceStep && step.from === 0 && step.to >= oldState.doc.content.size - 1
          )

          // Yjs sync transactions replace the entire document without standard
          // ProseMirror steps, so also treat step-less doc changes as full replaces.
          if (isFullDocumentReplace || transaction.steps.length === 0) {
            return rebuildDecorations(newState.doc, pluginState.highlightedChunkIds, pluginState.highlightType)
          }

          // If decorations were last built from an empty/skeleton document and
          // the document now has real content, rebuild immediately so citations
          // appear without waiting for the debounced rebuild.
          if (!pluginState.decorationsBuiltWithContent && newState.doc.content.size > EMPTY_DOCUMENT_SIZE_THRESHOLD) {
            return rebuildDecorations(newState.doc, pluginState.highlightedChunkIds, pluginState.highlightType)
          }

          return {
            decorations: pluginState.decorations.map(transaction.mapping, newState.doc),
            highlightedChunkIds: pluginState.highlightedChunkIds,
            highlightType: pluginState.highlightType,
            decorationsBuiltWithContent: newState.doc.content.size > EMPTY_DOCUMENT_SIZE_THRESHOLD,
            rebuildPending: true,
          }
        }

        return pluginState
      },
    },

    view() {
      let rebuildTimer: ReturnType<typeof setTimeout> | null = null

      return {
        update(view: EditorView) {
          const pluginState = citationPluginKey.getState(view.state) ?? null

          if (pluginState !== null && pluginState.rebuildPending) {
            if (rebuildTimer !== null) {
              clearTimeout(rebuildTimer)
            }

            rebuildTimer = setTimeout(() => {
              rebuildTimer = null

              const currentState = citationPluginKey.getState(view.state) ?? null

              if (currentState !== null && currentState.rebuildPending) {
                view.dispatch(view.state.tr.setMeta(citationPluginKey, { type: 'REBUILD' } satisfies CitationMeta))
              }
            }, CITATION_REBUILD_DEBOUNCE_MS)
          }
        },

        destroy() {
          if (rebuildTimer !== null) {
            clearTimeout(rebuildTimer)
          }
        },
      }
    },

    props: {
      decorations(state) {
        return citationPluginKey.getState(state)?.decorations ?? DecorationSet.empty
      },
    },
  })
}
